import React, { useRef, useState } from "react";
import { Button, Typography, Box, CircularProgress, Alert } from "@mui/material";

function UploadComponent({ onUpload, disabled, format = "xlsx" }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const isValidFile = (file) => {
    if (!file) return false;
    const name = file.name || "";
    const lower = name.toLowerCase();
    return lower.endsWith(`.${format}`);
  };

  const handleFileChange = (event) => {
    setError("");
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!isValidFile(file)) {
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = "";
      setError(`Please select a .${format} file.`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setError("");
    setIsUploading(true);

    try {
      await onUpload(selectedFile);

      // Clear selection and reset UI to initial state
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectClick = () => {
    if (disabled || isUploading) return;
    if (inputRef.current) inputRef.current.click();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <input
        ref={inputRef}
        id="upload-file"
        type="file"
        accept={`.${format}`}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {isUploading ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1 }}>Uploading...</Typography>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={handleSelectClick}
          disabled={disabled}
        >
          Select {format.toUpperCase()} File
        </Button>
      )}

      {/* Show selected file name only when a file is selected and not uploading */}
      {selectedFile && !isUploading && (
        <Typography sx={{ mr: 2 }}>{selectedFile.name}</Typography>
      )}

      {/* Upload button visible always but disabled when no file selected or while uploading */}
      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={!selectedFile || disabled || isUploading}
      >
        Upload
      </Button>

      {error && (
        <Box sx={{ width: "100%" }}>
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        </Box>
      )}
    </Box>
  );
}

export default UploadComponent;
